import { BaseButton, DocumentViewer, Spinner } from '../../../..';
import { mdiArrowLeft, mdiFileTree } from '@mdi/js';
import Icon from '@mdi/react';
import React, { useEffect, useState } from 'react';
import { IDocumentDetailProps } from './document-detail.types';
import * as S from '../claim-timeline.styles';
import * as Card from './timeline-detail-card.styles';


export const DocumentDetail: React.FunctionComponent<IDocumentDetailProps> = (
    props: IDocumentDetailProps
): React.ReactElement => {
    const { document, appType, onLoadDocumentUrl, loading: externalLoading = false } = props;

    const [internalLoading, setInternalLoading] = useState(false);
    const [documentUrl, setDocumentUrl] = useState<string | undefined>();
    const [loadFailed, setLoadFailed] = useState(false);
    // A split document points at the document(s) it was cut from. While one is set, the viewer shows that
    // parent instead of the document itself — the same thing Support's "Show Source Documents" opens.
    const [parentDocumentId, setParentDocumentId] = useState<string | undefined>();

    const sourceDocumentIds = document.sourceDocumentIds ?? [];

    useEffect(() => {
        setParentDocumentId(undefined);
    }, [document.id]);

    useEffect(() => {
        const documentId = parentDocumentId ?? document?.id;
        if (!documentId) {
            return;
        }
        let cancelled = false;
        (async (): Promise<void> => {
            setInternalLoading(true);
            setLoadFailed(false);
            setDocumentUrl(undefined);
            try {
                // The parent's type is unknown here; the URL lookup only needs its id.
                const url = await onLoadDocumentUrl(documentId, parentDocumentId ? undefined : document.documentType);
                if (!cancelled) {
                    setDocumentUrl(url?.url);
                }
            } catch {
                if (!cancelled) {
                    setLoadFailed(true);
                }
            } finally {
                if (!cancelled) {
                    setInternalLoading(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [document, parentDocumentId, onLoadDocumentUrl]);

    const renderSourceDocumentActions = (): React.ReactElement | null => {
        if (parentDocumentId) {
            return (
                <Card.CdActionsRow>
                    <BaseButton variant="outlined" size="compact" onClick={() => setParentDocumentId(undefined)}>
                        <Icon path={mdiArrowLeft} size={0.6} />
                        Back to document
                    </BaseButton>
                    <Card.CdFactMuted>
                        Showing parent document{appType === 'support' ? ` ${parentDocumentId}` : ''}
                    </Card.CdFactMuted>
                </Card.CdActionsRow>
            );
        }
        if (sourceDocumentIds.length === 0) {
            return null;
        }
        return (
            <Card.CdActionsRow>
                {sourceDocumentIds.map((id, index) => (
                    <BaseButton key={id} variant="outlined" size="compact" onClick={() => setParentDocumentId(id)}>
                        <Icon path={mdiFileTree} size={0.6} />
                        {sourceDocumentIds.length > 1 ? `View parent document ${index + 1}` : 'View parent document'}
                    </BaseButton>
                ))}
            </Card.CdActionsRow>
        );
    };

    const renderViewer = (): React.ReactElement | null => {
        if (internalLoading) {
            return <Spinner />;
        }
        if (loadFailed) {
            return (
                <Card.CdSectionCard $embedded>
                    <Card.CdFactMuted>
                        {parentDocumentId
                            ? 'The parent document could not be loaded.'
                            : 'The document could not be loaded.'}
                    </Card.CdFactMuted>
                </Card.CdSectionCard>
            );
        }
        if (!documentUrl) {
            return null;
        }
        return (
            <Card.CdSectionCard $embedded>
                <DocumentViewer url={documentUrl} />
            </Card.CdSectionCard>
        );
    };

    return (
        <Card.CdPanel $embedded>
            {externalLoading ? (
                <Spinner />
            ) : (
                <>
                    <Card.CdSectionCard $embedded>
                        {appType === 'provider' && (
                            <S.ExpandTable>
                                <S.ExpandTBody>
                                    <S.ExpandTR>
                                        <S.ExpandTD>{document.description}</S.ExpandTD>
                                    </S.ExpandTR>
                                </S.ExpandTBody>
                            </S.ExpandTable>
                        )}

                        {appType === 'support' && (
                            <S.ExpandTable>
                                <S.ExpandTBody>
                                    <S.ExpandTR>
                                        <S.ExpandTH>ID</S.ExpandTH>
                                        <S.ExpandTD>{document.id}</S.ExpandTD>
                                    </S.ExpandTR>
                                    <S.ExpandTR>
                                        <S.ExpandTH>Description</S.ExpandTH>
                                        <S.ExpandTD>{document.description}</S.ExpandTD>
                                    </S.ExpandTR>
                                    <S.ExpandTR>
                                        <S.ExpandTH>Type</S.ExpandTH>
                                        <S.ExpandTD>{document.documentType}</S.ExpandTD>
                                    </S.ExpandTR>
                                    <S.ExpandTR>
                                        <S.ExpandTH>Filename</S.ExpandTH>
                                        <S.ExpandTD>{document.filename}</S.ExpandTD>
                                    </S.ExpandTR>
                                </S.ExpandTBody>
                            </S.ExpandTable>
                        )}

                        {renderSourceDocumentActions()}
                    </Card.CdSectionCard>

                    {renderViewer()}
                </>
            )}
        </Card.CdPanel>
    );
};
